import { createContext } from "react";

import { ProcessedSubmission } from "sj-ui-toolkit/src/api/types";
import { ServiceError, SandboxLessonDetails } from "sj-ui-toolkit/src/client";

import { SandboxFilesystem } from "./Filesystem/Filesystem";
import { type DirectoryItem_File } from "./Filesystem/fsTypes";
import {
  type CodeSubHintResponse,
  type HintUsageResponse,
  type CtfSubmissionResponse,
  type ReplParam,
  type SandboxStatus,
} from "sj-ui-toolkit/src/client";
import { type TestStatus } from "./Testing/InternalTestingHook";
import { type ProxyRequest } from "./Proxy/ProxyTypes";

import { HighlightedHintId } from "./AiHints";
import { SandboxOutputLine } from "./Output/OutputTypes";

export interface TestingState {
  testsAvailable: boolean;
  testsRunning: boolean;
  testsPassed: boolean;
}

export interface EditorState {
  currentPath: string | null;
  currentContents: string | null;
  serverContents: string | null;
  isDirty: boolean;
}

export type ClientStatus = "initial" | "acquiring" | "ready" | "error" | "resetting";
export type AcquiringStatus = "requesting" | "polling" | "acquired" | "error";
export type TimeoutStatus = "active" | "expiring" | "expired" | undefined;

export interface ClientSessionState {
  overall: ClientStatus;
  timeout: TimeoutStatus;
  sandbox: SandboxStatus | null;
  error: ServiceError | Error | null;
  readyTimestamp: Date | null;
  totalSecondsActive: number;
  replParam: ReplParam | undefined;
}

export const SandboxContext = createContext({
  lessonKey: "" as string,
  lessonDetails: null as SandboxLessonDetails | null,
  currentLanguage: null as string | null,
  isSandboxLessonComplete: false as boolean,

  // Sandbox claim state
  session: {
    overall: "initial" as ClientStatus,
    timeout: "active" as TimeoutStatus,
    sandbox: null as SandboxStatus | null,
    error: null as ServiceError | Error | null,
    readyTimestamp: null as Date | null,
    totalSecondsActive: 0 as number,
    replParam: undefined as ReplParam | undefined,
  } as ClientSessionState,

  acquireSandbox: (_engine: string) => {},
  resetSandbox: (_engine?: string) => {},
  abandonSandbox: () => {},

  hasTests: false as boolean,
  hasEditor: false as boolean,
  beginEditingFile: async (_path: string) => {},
  handleEditorChange: (_newContents: string) => {},
  saveCurrentFile: async () => false as boolean,
  filesystem: null as SandboxFilesystem | null,
  fileLookup: {} as { [path: string]: DirectoryItem_File },
  editor: {
    currentPath: null,
    currentContents: null,
    serverContents: null,
    isDirty: false,
  } as EditorState,

  runTests: async () => null as object | null | undefined,
  isTestRunning: false as boolean,
  testRequestError: null as Error | null,
  testStatus: null as TestStatus | null,

  submissions: [] as ProcessedSubmission[],
  newSubmission: null as ProcessedSubmission | null,

  aiHintsEnabled: false as boolean,
  hints: [] as CodeSubHintResponse[],
  hintsLoading: false,
  hintsLoaded: false,
  hintsError: null as Error | null,
  highlightedHint: null as HighlightedHintId | null,
  setHighlightedHint: (_hintId: HighlightedHintId | null) => {},
  generateAiHint: async (_submissionId: string) => null as CodeSubHintResponse | null,
  provideHintFeedback: async (_params: {
    submissionId: string;
    helpful: boolean;
    comment?: string;
  }) => {},

  // Proxy state & methods
  isIntercepting: false as boolean,
  requests: [] as ProxyRequest[],
  pendingRequestText: null as string | null,
  setInterceptState: async (_isIntercepting: boolean) => false as boolean,
  resumeInterceptedRequest: async (_requestText: string) => false as boolean,

  // Sandbox output
  outputLines: [] as SandboxOutputLine[],
  newLinesCount: 0 as number,
  resetNewLinesCount: () => {},
  clearOutput: () => {},

  // Session tracking
  maxStepCompleted: null as number | null,
  allStepsCompleted: false as boolean,
  correctFlagSubmitted: false as boolean,
  reportStepsCompleted: async (_num: number) => false as boolean,
  reportAllStepsCompleted: async () => {},
  useHint: async (_hintId: string) => null as HintUsageResponse | null,
  submitCtfProof: async (_proof: string) => null as CtfSubmissionResponse | null,
  onLessonFinish: () => {},
});
